<template>
  <v-alert
    v-if="showAlert && isAdminOrOwner"
    color="error"
    variant="tonal"
    density="compact"
    border="start"
    closable
    class="mb-4"
    @click:close="closeAlert"
  >
    <div class="d-flex align-center">
      <v-icon icon="mdi-alert-circle" class="mr-2" />
      <span>{{ $t("inventory.outOfStockAlert") }}</span>
      <v-spacer />
      <v-btn variant="text" color="error" class="ml-2" @click="viewOutOfStockItems">
        {{ $t("common.action.view") }}
      </v-btn>
    </div>
  </v-alert>
</template>

<script lang="ts" setup>
import { ref, onUnmounted, computed, watchEffect } from "vue";
import { useProductsStore } from "@/catalog/stores/products";
import { useAccountStore } from "@/account/stores/account";
import {
  collection,
  query,
  where,
  limit,
  onSnapshot,
  doc,
  DocumentReference,
  DocumentData,
  CollectionReference,
  Query,
  QuerySnapshot,
  DocumentSnapshot,
  FirestoreError,
} from "firebase/firestore";
import { db } from "@/app/plugins/firebase";
import { UserRoleStatus } from "@/shared/enums/status.enum";
import { hasRoleAccessToAction } from "@/shared/utils/role.utils";
import { useLocationStore } from "@/terminals/stores/locations";
import { storeToRefs } from "pinia";
import type { ProductFilterCondition } from "@/catalog/types/products";

interface InventoryStockData {
  hasInventory?: boolean;
  stockCount?: number;
}

const props = defineProps({
  productId: {
    type: String,
    default: null,
  },
});

const showAlert = ref(false);
let unsubscribe: null | (() => void) = null;
let unsubscribeGroup: Array<() => void> = [];

const accountId = computed(() => useAccountStore().account?.id);
const locationBasedInventoryEnabled = computed(() => {
  return (
    useAccountStore().account?.accountSettings?.inventory?.location_based_inventory_enabled === true
  );
});

const productsStore = useProductsStore();
const locationStore = useLocationStore();

const { selectedLocation } = storeToRefs(locationStore);

// Check if current user is admin or owner
const isAdminOrOwner = computed(() =>
  hasRoleAccessToAction([UserRoleStatus.ADMIN, UserRoleStatus.OWNER]),
);

onUnmounted(() => {
  if (unsubscribe) {
    unsubscribe();
  }
  unsubscribeGroup.forEach((handler) => handler());
  unsubscribeGroup = [];
});

const explicitLocationId = computed<null | string | undefined>(() => {
  const condition = productsStore.filterProductCondition;
  if (!condition || !("locationId" in condition)) {
    return undefined;
  }
  const value = condition.locationId;
  if (value === null) return null;
  return typeof value === "string" && value ? value : undefined;
});

const effectiveLocationId = computed<string | null>(() => {
  if (!locationBasedInventoryEnabled.value) return null;
  if (typeof explicitLocationId.value === "string") return explicitLocationId.value;
  if (explicitLocationId.value === null) return null;
  return selectedLocation.value?.id ?? null;
});

watchEffect((onCleanup) => {
  if (!isAdminOrOwner.value) {
    showAlert.value = false;
    return;
  }

  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
  unsubscribeGroup.forEach((handler) => handler());
  unsubscribeGroup = [];

  const id = accountId.value;
  if (!id) {
    showAlert.value = false;
    return;
  }

  const listenToDoc = (productDocRef: DocumentReference<DocumentData>) => {
    unsubscribe = onSnapshot(
      productDocRef,
      (docSnapshot: DocumentSnapshot<DocumentData>) => {
        if (!docSnapshot.exists()) {
          showAlert.value = false;
          return;
        }
        showAlert.value = isProductOutOfStock(docSnapshot.data());
      },
      (error: FirestoreError) => {
        console.error("Error listening to out of stock product:", error);
        showAlert.value = false;
      },
    );
  };

  const buildOutOfStockQuery = (productsRef: CollectionReference<DocumentData>) => {
    let productQuery: Query<DocumentData> = query(productsRef);
    productQuery = query(productQuery, where("stockCount", "<=", 0));
    productQuery = query(productQuery, where("hasInventory", "==", true));
    productQuery = query(productQuery, limit(1));
    return productQuery;
  };

  if (!locationBasedInventoryEnabled.value) {
    if (props.productId) {
      listenToDoc(doc(db, "accounts", id, "products", props.productId));
      return;
    }

    const productsRef = collection(db, "accounts", id, "products");
    const productQuery = buildOutOfStockQuery(productsRef);
    unsubscribe = onSnapshot(
      productQuery,
      (snapshot: QuerySnapshot<DocumentData>) => {
        showAlert.value = !snapshot.empty;
      },
      (error: FirestoreError) => {
        console.error("Error listening to out of stock items:", error);
        showAlert.value = false;
      },
    );
    return;
  }

  const locationId = effectiveLocationId.value;

  if (locationId) {
    const resolvedLocationId = locationId;

    if (props.productId) {
      listenToDoc(
        doc(db, "accounts", id, "locations", resolvedLocationId, "products", props.productId),
      );
      return;
    }

    const productsRef = collection(db, "accounts", id, "locations", resolvedLocationId, "products");
    const productQuery = buildOutOfStockQuery(productsRef);
    unsubscribe = onSnapshot(
      productQuery,
      (snapshot: QuerySnapshot<DocumentData>) => {
        showAlert.value = !snapshot.empty;
      },
      (error: FirestoreError) => {
        console.error("Error listening to out of stock items:", error);
        showAlert.value = false;
      },
    );
    return;
  }

  // When the Product Location filter is explicitly set to "Global", there is no single
  // location context, so do not show inventory alerts.
  if (explicitLocationId.value === null) {
    showAlert.value = false;
    return;
  }
  showAlert.value = false;
});

const isProductOutOfStock = (data: InventoryStockData | undefined): boolean => {
  const hasInventory = data?.hasInventory !== false;
  if (!hasInventory) {
    return false;
  }

  const stockCount = Number(data?.stockCount ?? 0);
  return stockCount <= 0;
};

const closeAlert = () => {
  showAlert.value = false;
};

const viewOutOfStockItems = () => {
  // Clear all existing filter conditions by creating a new empty object
  const filterProductCondition: ProductFilterCondition = {};
  const existingCondition = useProductsStore().filterProductCondition;
  if (existingCondition && "locationId" in existingCondition) {
    filterProductCondition.locationId = existingCondition.locationId;
  }

  // Set the out of stock filter
  filterProductCondition.outOfStock = true;
  filterProductCondition.hasInventory = true;

  // If a product ID is provided, filter for that specific product
  if (props.productId) {
    filterProductCondition.id = props.productId;
  }

  useProductsStore().filterProductCondition = filterProductCondition;
  useProductsStore().searchProducts({}, false);
};
</script>
